import type { Locale } from '@/strings/types';
import { useShellLocale } from './shellLocale';
import TouchDot from './TouchDot';
import {
  IconMic,
  IconVolume,
  IconPause,
  IconStop,
  IconPlay,
  IconSend,
  IconDownload,
  IconTrash,
  IconExternal,
  IconPin,
  IconPopIn,
  IconClose,
} from './icons';
import {
  DEMO_DURATION_S,
  formatRecTime,
  useRecorderDemoState,
  type RecorderDemo,
  type RecState,
} from './useRecorderDemo';
import './Recorder.css';

/* Copy is inline for the same reason AppShell's is: this is product chrome in a
   mockup, not site copy, and routing it through web/src/copy would imply it is
   subject to the copy-review process. It mirrors CHROME's shape, and every
   surface reads it through useShellLocale so the recorder always speaks the
   language of the shell it is docked in.

   ⚠️ AR IS A PROPOSAL PENDING OWNER SIGN-OFF (style guide workflow, step 7). */
const REC: Record<Locale, Record<string, string>> = {
  en: {
    record: 'Record',
    rec: 'REC',
    /* The pop-out banner's own vocabulary, which is NOT the button's — the app's
       recorder.popoutStatus is IDLE / RECORDING / READY (i18n/locales/en.json). */
    idleBanner: 'Idle',
    recording: 'Recording',
    ready: 'Ready',
    process: 'Process',
    pin: 'Always on top',
    /* The window's own name (owner, 2026-09-06). A real Document
       Picture-in-Picture bar shows the ORIGIN — this was "app.prinotes.com" — but
       a bare hostname on a marketing page reads as a URL bar, not as a window
       that belongs to the product. Naming it says what it IS. */
    windowName: 'PRINOTES Recorder',
  },
  ar: {
    record: 'تسجيل',
    /* Left Latin deliberately. §1 bans abbreviations "where the full word fits"
       and §8 leaves an acronym in Latin "when there is no room for the
       expansion" — this badge is ~34px wide and sits beside a live red dot, so
       it is the §8 case. «تسجيل» would also collide with the button's label. */
    rec: 'REC',
    idleBanner: 'في الانتظار',
    /* §3's own example shape — «يجري الآن تنزيل البرنامج» — rather than «جارٍ
       التسجيل», which needs a kasratan §6 has no slot for. */
    recording: 'يجري التسجيل',
    ready: 'جاهز',
    process: 'معالجة',
    pin: 'تثبيت في المقدمة',
    /* §5: the Arabic heads the phrase and the brand keeps its locked
       transliteration, fatha on the ر included. Never «PRINOTES مسجل». */
    windowName: 'مسجل برَينوتس',
  },
};

/** The recorder's strings in the enclosing shell's language. */
const useRec = () => REC[useShellLocale()];

/* One mapping from session state to dot treatment, used by the collapsed rail
   and the pop-out's status banner alike — they were drifting when each spelled
   out its own ternary. Mirrors the app's PnRecDot states. */
const DOT_KIND: Record<RecState, 'idle' | 'live' | 'ready'> = {
  idle: 'idle',
  recording: 'live',
  done: 'ready',
};

/* ── The body, shared by both surfaces ────────────────────────────────────
   Mirrors RecorderBody.tsx's three-branch switch (idle / active / done) and its
   class names, so the mockup and the app stay legible against each other.

   Everything is a <span>: nothing in this shell is interactive, and a real
   <button> here would be a keyboard stop that does nothing. */
function RecorderStates({
  demo,
  variant,
  recRef,
  stopRef,
}: {
  demo: RecorderDemo;
  /** ONE switch for every difference between the two surfaces. They were two
      independent booleans and that is exactly how a surface ends up with the
      pop-out button AND the pop-out-only player controls. */
  variant: 'dock' | 'popout';
  recRef?: React.Ref<HTMLSpanElement>;
  stopRef?: React.Ref<HTMLSpanElement>;
}) {
  const rec = useRec();

  /* Dock only — a pop-out offering to pop itself out is a button to nowhere. */
  const popout =
    variant === 'dock' ? (
      <span className="pnrec__popoutbtn">
        <IconExternal size={14} />
      </span>
    ) : null;

  /* Pop-out only, matching RecorderBody's `extraControls={variant === 'popout'}`:
     the app gives the detached window playback speed and volume because it is
     where you actually sit and listen back. The 175px dock has no room and does
     not get them. */
  const extras =
    variant === 'popout' ? (
      <>
        <span className="pnrec__rate">1&times;</span>
        <span className="pnrec__vol">
          <IconVolume size={14} />
        </span>
      </>
    ) : null;

  if (demo.state === 'idle') {
    return (
      <div className="pnrec__idlerow">
        <span className="pnrec__recbtn" ref={recRef}>
          <span className="pnrec__dot pnrec__dot--idle" />
          <span className="pnrec__recbtn-label">{rec.record}</span>
        </span>
        {/* On the dock this makes the idle state IDENTICAL to the Record well
            tabs 1 and 2 show (AppShell's .pnshell__recordwell): dot, label,
            pop-out glyph, no inner button chrome. The recorder should read as
            that same well waking up, not as a different component. */}
        {popout}
      </div>
    );
  }

  if (demo.state === 'recording') {
    return (
      <div className="pnrec__active">
        <div className="pnrec__statusrow">
          <span className="pnrec__status">
            <span className="pnrec__dot pnrec__dot--live" />
            {rec.rec}
          </span>
          <span className="pnrec__timer">{formatRecTime(demo.elapsed)}</span>
          {popout}
        </div>
        <div className="pnrec__sources">
          {/* 16px inside a 30px circle is the app's ratio
              (recorder.css: .pn-rec-src svg { width: 16px }). The stop glyph
              runs smaller because it is a solid block, and a filled 16 reads
              heavier than a stroked 16 at the same box. */}
          <span className="pnrec__src is-on">
            <IconMic size={16} />
          </span>
          <span className="pnrec__src is-on">
            <IconVolume size={16} />
          </span>
          <span className="pnrec__spacer" />
          <span className="pnrec__ctrl">
            <IconPause size={15} />
          </span>
          <span className="pnrec__ctrl pnrec__ctrl--stop" ref={stopRef}>
            <IconStop size={13} />
          </span>
        </div>
        {/* Level meters. The app drives these from an AnalyserNode at 60fps;
            here they are CSS keyframes with offset delays, because rAF-driven
            React state would re-render the whole demo 60 times a second on the
            marketing homepage to animate two rectangles. */}
        <div className="pnrec__meters">
          <span className="pnrec__meter pnrec__meter--mic">
            <span className="pnrec__meter-label">
              <IconMic size={14} />
            </span>
            <span className="pnrec__meter-track">
              <span className="pnrec__meter-fill" />
            </span>
          </span>
          <span className="pnrec__meter pnrec__meter--sys">
            <span className="pnrec__meter-label">
              <IconVolume size={14} />
            </span>
            <span className="pnrec__meter-track">
              <span className="pnrec__meter-fill" />
            </span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="pnrec__done">
      <div className="pnrec__done-info">
        <IconMic size={16} />
        <span className="pnrec__done-label">{rec.ready}</span>
        <span className="pnrec__done-dur">{formatRecTime(DEMO_DURATION_S)}</span>
        {popout}
      </div>
      {/* The player is its own RECESSED box, not a bare row — the app insets it
          on the content ground inside the teal-tinted done panel, which is what
          separates "the clip" from the actions under it. */}
      <div className="pnrec__player">
        <span className="pnrec__play">
          <IconPlay size={13} />
        </span>
        <span className="pnrec__scrub-time pnrec__scrub-time--cur">0:00</span>
        <span className="pnrec__scrub">
          <span className="pnrec__scrub-track">
            <span className="pnrec__scrub-thumb" />
          </span>
        </span>
        <span className="pnrec__scrub-time pnrec__scrub-time--dur">
          0:0{DEMO_DURATION_S}
        </span>
        {extras}
      </div>
      <div className="pnrec__actions">
        {/* The send glyph is NOT optional on the narrow dock. It was being
            dropped there — an svg is a flex item and the dock's Process button
            is the flexible one, so the icon got squeezed to nothing while the
            label held its width. `flex: none` in the CSS is the actual fix; the
            markup was always identical across both surfaces. */}
        <span className="pnrec__process">
          <IconSend size={15} />
          {rec.process}
        </span>
        <span className="pnrec__iconbtn">
          <IconDownload size={16} />
        </span>
        <span className="pnrec__iconbtn pnrec__iconbtn--danger">
          <IconTrash size={16} />
        </span>
      </div>
    </div>
  );
}

/**
 * The dock — the recorder as it sits in the sidebar footer, replacing the
 * Record well. Only rendered while the sidebar is expanded: below the app's
 * 760px threshold the sidebar is a 64px icon rail with nowhere to put a
 * transport, which is exactly why the pop-out exists.
 */
export function RecorderDock() {
  const demo = useRecorderDemoState();
  const rec = useRec();
  if (!demo) return null;

  return (
    <div className="pnrec pnrec--dock" data-state={demo.state}>
      {/* COLLAPSED rail state. It used to fall back to AppShell's plain Record
          well, whose badge dot is a static red — so on a narrow shell the
          recorder claimed "idle" through the whole sequence, and still claimed
          it after the clip was ready (owner, 2026-09-06). Rendering it here
          instead is what gives it access to the session state; the dot takes the
          same three colours as everywhere else, teal included. */}
      <span
        className="pnrec__rail"
        title={rec.record}
        ref={demo.targets.railRecord}
      >
        <span className="pnrec__railicon">
          <IconMic size={20} />
          <span className={`pnrec__dot pnrec__dot--${DOT_KIND[demo.state]}`} />
        </span>
      </span>
      <div className="pnrec__body">
        <RecorderStates
          demo={demo}
          variant="dock"
          recRef={demo.targets.dockRecord}
        />
      </div>
    </div>
  );
}

/**
 * The topbar record button — the stacked band's Record affordance.
 *
 * Below 480 the sidebar is gone entirely, taking both the Record well and the
 * collapsed rail's mic with it. This puts that same control back, beside the
 * hamburger, so the flow at this width is the one every other width shows:
 * press Record, the window appears.
 *
 * It is the RAIL button relocated, not a new idea — 32x32, mic glyph, session
 * dot badged on the corner, exactly .pn-rec-rail__btn's anatomy (the app's
 * recorder.css:490). Keeping one shape for "the recorder, collapsed" means the
 * two only differ in where they sit.
 *
 * Hidden above 480 by CSS, on the same container query that brings the sidebar
 * back — so the shell never offers two Record buttons at once.
 */
export function RecorderTopButton() {
  const demo = useRecorderDemoState();
  const rec = useRec();
  if (!demo) return null;

  return (
    <span
      className="pnrec__topbtn"
      data-state={demo.state}
      title={rec.record}
      ref={demo.targets.topRecord}
    >
      <IconMic size={18} />
      <span className={`pnrec__dot pnrec__dot--${DOT_KIND[demo.state]}`} />
    </span>
  );
}

/**
 * The pop-out — the recorder in its own always-on-top window.
 *
 * ⚠️ It does NOT exist until recording starts. That is the app's real flow, from
 * the owner's screen recordings (2026-09-06): at rest there is only the Record
 * well in the sidebar; pressing it starts the session, and the floating window
 * is spawned from there. The demo folds the app's separate "pop out" click into
 * the record press, the same way it skips Chrome's share-picker dialog — both
 * are steps a marketing loop cannot show without becoming a tutorial.
 *
 * The one exception is the stacked band, where there is no sidebar at all and so
 * nothing to press instead; there the window IS the recorder from the start.
 * That is a CSS rule on the same container query that removes the sidebar, not a
 * second copy of the threshold in JS.
 */
export function RecorderPopout() {
  const demo = useRecorderDemoState();
  const rec = useRec();
  if (!demo) return null;

  return (
    <div className="pnrec-popout" data-state={demo.state}>
      {/* The floating window's title bar. Pinned ltr — a real OS/browser window
          bar is not mirrored for our users.

          The controls are the ones this window actually has: BACK TO TAB (which
          returns the recorder to the sidebar dock) and CLOSE. It briefly showed
          a pop-out glyph, which is the button that CREATED this window and
          cannot exist inside it (owner, 2026-09-06).

          The pin is the only non-literal element, and it is carrying the
          "always on top" claim on its own — see IconPin. */}
      <div className="pnrec-popout__bar" dir="ltr">
        <span className="pnrec-popout__title">
          <span className="pnrec-popout__pin" title={rec.pin}>
            <IconPin size={13} />
          </span>
          {rec.windowName}
        </span>
        <span className="pnrec-popout__wc" aria-hidden="true">
          <IconPopIn size={13} />
          <IconClose size={13} />
        </span>
      </div>

      <div className="pnrec pnrec--popout">
        <div className={`pnrec-popout__status pnrec-popout__status--${demo.state}`}>
          <span className={`pnrec__dot pnrec__dot--${DOT_KIND[demo.state]}`} />
          {demo.state === 'recording'
            ? rec.recording
            : demo.state === 'done'
              ? rec.ready
              : rec.idleBanner}
        </div>
        <div className="pnrec__body">
          <RecorderStates
            demo={demo}
            variant="popout"
            recRef={demo.targets.popoutRecord}
            stopRef={demo.targets.popoutStop}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * The touch dot, at SHELL level.
 *
 * It used to live inside the pop-out and be positioned in that panel's own
 * coordinate space, which worked only while every press happened there. The
 * corrected flow presses record on the DOCK, in the sidebar, and stop in the
 * pop-out, over in the content area — two different components — so the dot
 * needs a coordinate space that contains both.
 *
 * The travel/press/entry behaviour now lives in the shared TouchDot, which tab 4
 * drives from its own script. What stays here is the only part that is about the
 * RECORDER: which of the four candidate surfaces the dot should press.
 */
export function RecorderTouchDot() {
  const demo = useRecorderDemoState();
  if (!demo) return null;

  /* Whichever record surface is actually on screen. Order is widest-first, and
     `offsetParent` is the cheap "is it laid out" test — the ones that are not
     are `display: none` behind a container query. Resolving by visibility keeps
     the choice on the same container queries that decide the layout, instead of
     a second copy of those thresholds in JS. */
  const resolve = () => {
    const { targets, aimAt } = demo;
    const vis = (el: HTMLElement | null) => (el && el.offsetParent !== null ? el : null);
    return aimAt === 'stop'
      ? vis(targets.popoutStop.current)
      : vis(targets.dockRecord.current) ??
          vis(targets.railRecord.current) ??
          vis(targets.topRecord.current) ??
          vis(targets.popoutRecord.current);
  };

  return (
    <TouchDot
      aim={demo.aimAt}
      resolve={resolve}
      visible={demo.dotVisible}
      pressing={demo.pressing}
    />
  );
}
